import { Component } from '@theme/component';
import { mediaQueryLarge, prefersReducedMotion } from '@theme/utilities';
import { getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';

/**
 * Mobile card-deck swipe + desktop per-column parallax for the Editorial
 * product stack widget.
 *
 * Same mechanic as assets/product-editorial.js (Widget 06) — forked rather than
 * reused directly so this section's own desktop 2-column card grid (see the
 * section's stylesheet) can safely define `.editorial-product-stack__cards-grid`
 * without fighting product-editorial.liquid's own (unscoped) rules for
 * `.product-editorial__wrapper` when both sections happen to render on the same
 * page. Only the wrapper selector and custom element tag differ from the
 * original; the stacking/gesture logic is identical. `.product-editorial-card`
 * itself is untouched — it comes from the shared `_product-editorial-card`
 * block this section also renders, so no selector change was needed there.
 *
 * Desktop stays a plain, JS-free CSS grid; below the 768px breakpoint the same
 * `_product-editorial-card` blocks become a swipeable stack.
 *
 * No library involved. Every card in `.editorial-product-stack__cards-grid`
 * sits in the *same* CSS grid cell (`grid-area: 1 / 1` — see the section's own
 * stylesheet) below that breakpoint, so they're already visually stacked with
 * no JS needed for that part; this file only tracks which one is currently
 * "active" and animates the move to a neighbor on a swipe, in either direction
 * (`#advance`/`#retreat` — same mechanism as assets/collection-reel.js's own
 * bidirectional stack).
 *
 * Depth (`depthOf`) is always computed modulo the real card count, relative to
 * whichever index is active — that alone is what makes the stack a perpetual
 * cycle with a *fixed* number of cards, no separate "loop" concept needed: the
 * card that just left always reappears at the back of the stack, forever,
 * since its depth is just `(cardIndex - activeIndex + total) % total`.
 *
 * A forward swipe's active card slides down-and-left, rotates and fades out
 * as it's dismissed — the one and only place rotation ever appears, driven by the
 * `editorial-product-stack-card-exit` CSS keyframe animation (added via the
 * `.exiting` class) rather than by this file directly, so the easing is free
 * browser-native keyframe interpolation rather than a rAF loop. That
 * keyframe's last step lands exactly on the plain resting transform for the
 * card's new (back-of-stack) depth — via `--rest-y`/`--rest-scale`/
 * `--rest-opacity`, set inline right before it starts — so a card is never
 * left resting at an angle. Every other card just gets its new resting depth
 * directly; the section's own CSS `transition` on transform/opacity smoothly
 * animates that (stack shifting up one level, next card growing to full size)
 * without any keyframes at all.
 *
 * Desktop's per-column "asynchronous scroll" (spec §4) is the same catch-up
 * idea sections/category-explorer.liquid uses for its own two columns: one
 * column starts offset from the other and closes that gap as the visitor
 * scrolls, reading as the faster of the two. category-explorer earns that
 * with two independent flex columns + spacers whose `flex-grow` migrates via
 * `animation-timeline: view()` — not available here, since this section's
 * card blocks come from a single `content_for 'blocks'` call and can only be
 * rendered once (see the section file's own top comment), so there is no
 * second column container to give its own spacers. The right (even) column's
 * *rest* offset is a plain static CSS rule instead (`inset-block-start: 90px`
 * — see the section's stylesheet); this file only animates that gap closed
 * on scroll, via a `translate` custom property on the same cards.
 *
 * That animation is a plain scroll listener rather than
 * `animation-timeline: view()` (as category-explorer itself uses): that
 * API's browser support is still not universal, so a merchant on a browser
 * without it (or anyone whose environment reports `prefers-reduced-motion`
 * unexpectedly) saw *nothing* — not a weaker effect, no catch-up whatsoever,
 * with no way to tell the two apart from the storefront, and no CSS rest
 * state to fall back to the way category-explorer's own spacers do (theirs
 * default to top-aligned, an equally valid look; this section's static
 * `inset-block-start: 90px` is not — it's *only* correct as the starting
 * point of an animation that closes it). A `scroll`/`resize` listener works
 * identically in every evergreen browser, so the effect is guaranteed rather
 * than conditional on feature support. Reduced motion is still respected
 * (checked live on every update, same as `#advance()` below) since that one
 * dependency is an accessibility preference, not a fragile browser-support
 * gate — with it on, the right column simply stays at its static, offset
 * rest position instead of animating flush with the left one.
 *
 * The theme keeps `<html>`/`<body>` non-scrolling above the 990px squeeze
 * breakpoint (see `html:has(.page-wrapper)` in assets/base.css) and scrolls
 * `.page-wrapper` instead, while narrower widths fall back to the document
 * itself scrolling. `assets/scroll-container.js`'s `getScrollEventTarget()`
 * already resolves that (same as assets/header.js's own sticky-scroll
 * listener), rebound via its `scrollContainerMediaQuery` on the 990px
 * boundary rather than `mediaQueryLarge` — the two breakpoints (768px vs
 * 990px) don't coincide, so the catch-up can stay active across a resize
 * that crosses 990px while never leaving desktop mode. `#applyParallax`
 * itself still recomputes purely from `getBoundingClientRect()` either way
 * (viewport-relative, correct regardless of which ancestor actually
 * scrolled).
 */

// Every card in the deck stays visible (not just the top few — this section
// allows up to 8 blocks, unlike Product editorial's fixed 3-card max, so a
// small fixed lookup table can't cover every possible deck size), each one
// peeking STACK_DEPTH_OFFSET_PX above the card in front of it — a constant
// per-card step matching the stack's own dynamic padding-top
// (`section.blocks.size * 20px`, set inline by
// sections/editorial-product-stack.liquid), so the deepest card's peek always
// lines up with the reserved space above the stack. Cards further back also
// scale down a little per depth (STACK_DEPTH_SCALE_STEP, floored at
// STACK_MIN_SCALE) for a subtle receding effect — see restStyleForDepth for
// why translateY alone isn't the true peek height once scale is involved.
const STACK_DEPTH_OFFSET_PX = 20;
const STACK_DEPTH_SCALE_STEP = 0.05;
const STACK_MIN_SCALE = 0.7;
// Must match the animation's own duration and 55% checkpoint in the
// `editorial-product-stack-card-exit` keyframes (section stylesheet) — that's
// where the leaving card stops sliding out and reverses back toward its
// resting spot in the stack.
const TRANSITION_MS = 550;
const EXIT_REVERSAL_FRACTION = 0.55;
// Minimum deliberate horizontal drag, in px, to count as a forward swipe
// rather than an incidental tap or a vertical page scroll.
const SWIPE_THRESHOLD_PX = 40;
// Any movement below this, in px (either axis), still counts as a stationary
// tap rather than a drag — accounts for natural finger/mouse jitter so a real
// tap on the card's image link still navigates normally (see `#handleClick`).
const TAP_MOVE_TOLERANCE_PX = 10;
// Once total movement passes this, in px, the gesture's direction is locked in
// from whichever axis dominates so far — see `#handlePointerMove`.
const DIRECTION_LOCK_MOVE_PX = TAP_MOVE_TOLERANCE_PX;
// Delay, in ms, before the one-shot "swipeable" hint plays after the stack's
// first render (`#playSwipeHint`) — long enough for assets/motion.js's own
// entrance fade/rise (`--motion-reveal-duration`, motion.css) and this file's
// own resting `transition` to settle first, so the hint never competes with
// either for the same `transform` property.
const SWIPE_HINT_DELAY_MS = 1200;
// The right (even) column's static rest offset (`inset-block-start: 90px` in
// the section's own stylesheet). A 100%-strength catch-up travels twice this
// — see `#applyParallax`'s own comment for why — ending the column this far
// *above* the left one instead of merely flush with it.
// `.editorial-product-stack__cards-col`'s own `padding-bottom: 120px` is a
// separate number — a buffer with 30px to spare beyond this stagger, not a
// value meant to be kept in sync with it.
const CATCHUP_STAGGER_PX = 90;
// Fallback header height, in px, if `--header-height` (assets/header.js,
// kept live on `document.body`) isn't set yet — matches that property's own
// fallback everywhere else it's read with a default.
const FALLBACK_HEADER_HEIGHT_PX = 100;
// Floor on `#applyParallax`'s scroll-through span (see its own comment) —
// guarantees a smooth ramp even for a widget shorter than the viewport,
// rather than the catch-up snapping instantly to its end state.
const MIN_CATCHUP_SPAN_PX = 300;

/**
 * Pure function: given how far behind the active card a slide is (how many
 * cards are in the deck, and their shared rendered height), return the
 * resting transform/z-index for that depth. Never includes rotation — the
 * resting stack is always perfectly straight, offset only vertically.
 * Opacity is always 1: every card in the deck stays visible.
 *
 * `scale()` uses `transform-origin: center` (the default) — shrinking a card
 * moves its top edge DOWN by `(1 - scale) * cardHeight / 2` at the same time
 * translateY tries to move it up, so a naive `-depth * STACK_DEPTH_OFFSET_PX`
 * translate alone would leave a thinner peek at every depth than intended
 * (worse the more it's scaled down). Baking that shift into `y` here keeps
 * the actual visible peek an exact, uniform STACK_DEPTH_OFFSET_PX at every
 * depth regardless of how much smaller the card gets.
 * @param {number} depth
 * @param {number} total
 * @param {number} cardHeight - Rendered height of a stack card
 * (`offsetHeight` — layout size, unaffected by any transform already applied
 * to it).
 */
function restStyleForDepth(depth, total, cardHeight) {
  const scale = Math.max(STACK_MIN_SCALE, 1 - depth * STACK_DEPTH_SCALE_STEP);
  const scaleShift = ((1 - scale) * cardHeight) / 2;
  const y = -depth * STACK_DEPTH_OFFSET_PX - scaleShift;
  const zIndex = total - depth;
  return { y, scale, opacity: 1, zIndex };
}

/**
 * @param {HTMLElement} cardEl
 * @param {number} depth
 * @param {number} total
 * @param {number} cardHeight
 */
function applyRest(cardEl, depth, total, cardHeight) {
  const { y, scale, opacity, zIndex } = restStyleForDepth(depth, total, cardHeight);
  cardEl.style.transform = `translate(0px, ${y}px) rotate(0deg) scale(${scale})`;
  cardEl.style.opacity = String(opacity);
  cardEl.style.zIndex = String(zIndex);
}

/**
 * Every stack depth stays visually present (only offset/scaled, never
 * `display: none` — see `restStyleForDepth`), so a card's own `<video
 * autoplay loop>` (`_product-editorial-card.liquid`) would otherwise decode
 * and loop forever even while ~90% obscured behind the active card. Only the
 * active (depth 0) card's video plays; every other depth is paused.
 * @param {HTMLElement} cardEl
 * @param {number} depth
 */
function syncVideoPlayback(cardEl, depth) {
  const video = /** @type {HTMLVideoElement | null} */ (cardEl.querySelector('video'));
  if (!video) return;

  // Reduced-motion already paused (and removed `autoplay` from) the video on
  // load (`_product-editorial-card.liquid`'s own script) — never override
  // that by playing it back just because it became the active card.
  if (depth === 0 && !prefersReducedMotion()) {
    video.play();
  } else {
    video.pause();
  }
}

/**
 * @param {number} cardIndex
 * @param {number} activeIndex
 * @param {number} total
 */
function depthOf(cardIndex, activeIndex, total) {
  return (((cardIndex - activeIndex) % total) + total) % total;
}

export class EditorialProductStackCards extends Component {
  /** @type {HTMLElement[]} */
  #cards = [];
  #activeIndex = 0;
  #isAnimating = false;
  #pointerDownX = 0;
  #pointerDownY = 0;
  // Invalidates any in-flight animation's cleanup callbacks once torn down
  // (breakpoint flipping back to desktop mid-exit), so they don't touch a
  // card this instance no longer considers part of the deck.
  #generation = 0;
  // Whether the gesture that just ended moved enough to count as a drag
  // rather than a stationary tap — checked by `#handleClick` so a swipe that
  // happened to end over the card's image link doesn't also navigate to the
  // product page. A real tap (no meaningful movement) still navigates.
  #wasDrag = false;
  // `null` until `DIRECTION_LOCK_MOVE_PX` of total movement has happened in
  // the current gesture, then locked to whichever axis dominates at that
  // point. Read by `#handleTouchMove` to decide whether *this* gesture should
  // cancel the section's `touch-action: pan-y` native scroll — see that
  // handler for why suppression has to happen from a real `touchmove`
  // listener rather than from `#handlePointerMove` itself.
  /** @type {'horizontal' | 'vertical' | null} */
  #dragDirection = null;
  // Set at pointerdown when a card animation (`#advance`/`#retreat`) is still in flight,
  // so the rest of that gesture is ignored outright instead of relying only on
  // `#advance`/`#retreat`'s own `#isAnimating` no-op — without this, a swipe that starts
  // mid-animation still tracks `#pointerDownX`/`#dragDirection` as if it were live, which
  // can wrongly suppress the section's native scroll (`#handleTouchMove`) or misjudge a
  // tap as a drag (`#handleClick`) once the in-flight animation finishes. Same guard as
  // assets/product-editorial.js and assets/collection-reel.js.
  #ignoringGesture = false;
  // Watches the first card for assets/motion.js's one-shot `motion-revealed`
  // class when `#init()` has to defer `#render()` until that fires — see
  // `#renderOnceRevealed`. Torn down early if `#destroy()` runs first (a
  // breakpoint flip back to desktop before the group ever scrolls into view),
  // so it can't fire `#render()` against an already-cleared deck.
  /** @type {MutationObserver | null} */
  #revealObserver = null;
  // The desktop catch-up's own state — kept entirely separate from `#cards`
  // above (the mobile deck's state), since the two features are active at
  // opposite breakpoints and would otherwise fight over what that field
  // means. `#parallaxLayout` is cached once on connect since it's an
  // ancestor outside this element (`.editorial-product-stack__layout`), not
  // something that can change without the component itself being
  // reconnected. Only the right (even) column ever animates — the left one
  // has no static rest offset to close, so it never needs tracking here.
  /** @type {HTMLElement | null} */
  #parallaxLayout = null;
  /** @type {HTMLElement[]} */
  #parallaxCatchupCards = [];
  // 0-1 fraction read once in `#startParallax` from the section's own
  // `--editorial-product-stack-catchup` custom property (Liquid sets it once
  // per render; it can't change for this component instance's lifetime) —
  // caching it here means `#applyParallax` never forces a style
  // recalculation for it on the scroll/resize hot path.
  #parallaxCatchupFraction = 0;
  #parallaxActive = false;
  // The element `scroll` is actually bound to — `.page-wrapper` above the
  // 990px squeeze breakpoint, `document` below it (assets/scroll-container.js
  // `getScrollEventTarget()`). Rebound on `scrollContainerMediaQuery` change
  // rather than cached for the component's lifetime, since that breakpoint
  // doesn't coincide with `mediaQueryLarge` (768px) — a resize can cross it
  // while the catch-up stays active the whole time.
  /** @type {EventTarget | null} */
  #parallaxScrollContainer = null;
  // rAF-coalesces bursts of `scroll`/`resize` events into at most one style
  // recalculation per frame.
  #parallaxTicking = false;

  connectedCallback() {
    super.connectedCallback();
    this.#parallaxLayout = this.closest('.editorial-product-stack__layout');
    mediaQueryLarge.addEventListener('change', this.#handleBreakpointChange);
    scrollContainerMediaQuery.addEventListener('change', this.#handleScrollContainerChange);
    this.#handleBreakpointChange();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleBreakpointChange);
    scrollContainerMediaQuery.removeEventListener('change', this.#handleScrollContainerChange);
    this.#destroy();
    this.#stopParallax();
  }

  #handleBreakpointChange = () => {
    if (mediaQueryLarge.matches) {
      this.#destroy();
      this.#startParallax();
    } else {
      this.#init();
      this.#stopParallax();
    }
  };

  // No-op unless the catch-up is currently active — see `#parallaxScrollContainer`'s
  // own comment for why this breakpoint can fire independently of `#handleBreakpointChange`.
  #handleScrollContainerChange = () => {
    if (!this.#parallaxActive) return;
    this.#parallaxScrollContainer?.removeEventListener('scroll', this.#requestParallaxUpdate);
    this.#parallaxScrollContainer = getScrollEventTarget();
    this.#parallaxScrollContainer.addEventListener('scroll', this.#requestParallaxUpdate, { passive: true });
  };

  #startParallax() {
    if (this.#parallaxActive || !this.#parallaxLayout) return;

    const section = this.closest('.editorial-product-stack');
    if (!section) return;

    // Not `#parallaxLayout.querySelectorAll` directly for the same reason
    // `#init()` above scopes through `:scope >` on the grid rather than the
    // wrapper's `.children` — each block can render its own sibling
    // `<style>`/`<script>` tag, which would otherwise throw off a
    // `:nth-of-type` count done by hand here.
    const wrapper = this.querySelector('.editorial-product-stack__cards-grid');
    if (!wrapper) return;
    const allCards = /** @type {HTMLElement[]} */ (
      Array.from(wrapper.querySelectorAll(':scope > .product-editorial-card'))
    );
    // Even position (2nd, 4th, ...) is the visual right column — the one the
    // section's stylesheet gives a static `inset-block-start: 90px` rest
    // offset, and the only one this file ever animates.
    this.#parallaxCatchupCards = allCards.filter((_, i) => i % 2 === 1);
    if (!this.#parallaxCatchupCards.length) return;

    this.#parallaxCatchupFraction =
      parseFloat(getComputedStyle(section).getPropertyValue('--editorial-product-stack-catchup')) || 0;

    this.#parallaxActive = true;
    this.#parallaxScrollContainer = getScrollEventTarget();
    this.#parallaxScrollContainer.addEventListener('scroll', this.#requestParallaxUpdate, { passive: true });
    window.addEventListener('resize', this.#requestParallaxUpdate);
    this.#applyParallax();
  }

  #stopParallax() {
    if (!this.#parallaxActive) return;

    this.#parallaxActive = false;
    this.#parallaxScrollContainer?.removeEventListener('scroll', this.#requestParallaxUpdate);
    this.#parallaxScrollContainer = null;
    window.removeEventListener('resize', this.#requestParallaxUpdate);

    for (const card of this.#parallaxCatchupCards) {
      card.style.removeProperty('--editorial-product-stack-card-offset');
    }
    this.#parallaxCatchupCards = [];
  }

  #requestParallaxUpdate = () => {
    if (this.#parallaxTicking) return;
    this.#parallaxTicking = true;
    requestAnimationFrame(() => {
      this.#parallaxTicking = false;
      this.#applyParallax();
    });
  };

  /**
   * Reduced motion checked fresh on every call (same pattern as
   * `#advance()`/`syncVideoPlayback` elsewhere in this file) rather than
   * once at `#startParallax()` time, so a preference flipped mid-session is
   * honoured on the very next scroll instead of only after the next
   * breakpoint change. With it on, `--editorial-product-stack-card-offset`
   * stays `0px` — the right column simply sits at its static
   * `inset-block-start: 90px` rest position (section stylesheet) and never
   * animates, same as category-explorer's own spacers defaulting to
   * top-aligned with no motion.
   */
  #applyParallax() {
    if (!this.#parallaxActive || !this.#parallaxLayout) return;

    if (prefersReducedMotion()) {
      for (const card of this.#parallaxCatchupCards) {
        card.style.setProperty('--editorial-product-stack-card-offset', '0px');
      }
      return;
    }

    // Travels `2 * CATCHUP_STAGGER_PX` at full strength, not just
    // `CATCHUP_STAGGER_PX`: the first half only cancels the static +90px
    // rest offset (landing flush with the left column, category-explorer's
    // own stopping point), and this section's own right column needs to read
    // as unambiguously the faster one rather than merely "no longer behind"
    // — so the second half carries it the same 90px past flush, ending it
    // that far *above* the left column instead. Symmetric on purpose: it
    // ends as far ahead as it started behind.
    const maxOffset = this.#parallaxCatchupFraction * (2 * CATCHUP_STAGGER_PX);
    if (maxOffset <= 0) return;

    // Spans the same range as category-explorer's own
    // `animation-range: exit-crossing calc(var(--header-height) * -1)
    // contain 100%`, computed by hand since `#applyParallax` is a scroll
    // listener rather than a native view-timeline (see this file's top
    // comment for why): progress 0 is the instant the layout's top passes
    // under the header (where category-explorer's media pins too), progress
    // 1 is the instant the layout's bottom edge rises into view — i.e. the
    // widget is about to finish scrolling past. Tied to the widget's own
    // (variable, content-driven) height rather than a short fixed distance,
    // so the catch-up plays out while it's actually being scrolled through
    // instead of resolving the moment it's first visible, however far down
    // the page it happens to sit.
    const headerPx =
      parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || FALLBACK_HEADER_HEIGHT_PX;
    const rect = this.#parallaxLayout.getBoundingClientRect();
    // How far the layout has to travel, top to bottom, past the two
    // progress anchors above — floored at `MIN_CATCHUP_SPAN_PX` rather than
    // left to go non-positive for a widget shorter than the viewport itself
    // (rare given its content, but a merchant could still hit it with the
    // minimum 4 cards on a short-header, tall viewport). Without the floor,
    // that case would make the right column snap straight to full
    // catch-up/overtake with no visible motion at all — exactly the
    // "reads as scrolling faster" effect this file exists to produce.
    const span = Math.max(rect.height - (window.innerHeight - headerPx), MIN_CATCHUP_SPAN_PX);
    const progress = Math.min(1, Math.max(0, (headerPx - rect.top) / span));
    // Negative: cancels the static `+90px` rest offset as it grows, moving
    // the right column up — on top of the page's own upward scroll motion,
    // so it reads as scrolling faster, same as category-explorer's shorter
    // column. The left (odd) column has no rest offset to close and never
    // moves at all, same as category-explorer's taller column.
    const offset = -(progress * maxOffset);

    for (const card of this.#parallaxCatchupCards) {
      card.style.setProperty('--editorial-product-stack-card-offset', `${offset}px`);
    }
  }

  #init() {
    if (this.#cards.length) return;

    const wrapper = this.querySelector('.editorial-product-stack__cards-grid');
    if (!wrapper) return;

    // Not `wrapper.children` — each block can render its own sibling `<style>`
    // tag (see `_product-editorial-card.liquid`'s `contrast-override` call for
    // a per-card background color), so the wrapper's direct children aren't
    // only cards.
    this.#cards = /** @type {HTMLElement[]} */ (
      Array.from(wrapper.querySelectorAll(':scope > .product-editorial-card'))
    );
    if (this.#cards.length < 2) return; // nothing to cycle

    this.#activeIndex = 0;
    // A prior #destroy() mid-exit-animation can't rely on the `animationend`
    // listener in #advance() to clear this — removing `.exiting` there fires
    // `animationcancel`, not `animationend` — so reset it explicitly here too.
    this.#isAnimating = false;
    this.#renderOnceRevealed(wrapper);

    this.addEventListener('pointerdown', this.#handlePointerDown);
    this.addEventListener('pointermove', this.#handlePointerMove);
    this.addEventListener('pointerup', this.#handlePointerUp);
    this.addEventListener('pointercancel', this.#handlePointerCancel);
    // `passive: false` so `preventDefault()` inside can actually cancel the
    // browser's native scroll. A real `touchmove` listener, not `pointermove`
    // — see `#handleTouchMove`'s own comment for why.
    this.addEventListener('touchmove', this.#handleTouchMove, { passive: false });
    // Each card's image links to its product (`_product-editorial-card
    // .liquid`) — an anchor is natively draggable, so a swipe starting on one
    // triggers the browser's own HTML5 drag-and-drop instead of a normal
    // pointer gesture, which cancels our pointerup entirely (fires
    // `pointercancel` instead). Suppressing native drag here is the only way
    // to keep swiping over the image working.
    this.addEventListener('dragstart', this.#handleDragStart);
    // Suppressing `dragstart` above means a swipe that ends over the image
    // link falls through to a normal click — capture phase so this runs
    // before the link's own navigation.
    this.addEventListener('click', this.#handleClick, true);
  }

  #destroy() {
    if (!this.#cards.length) return;

    this.#generation++;
    // Removing `.exiting` below cancels any in-progress exit animation via
    // `animationcancel`, not `animationend` — the listener in #advance() that
    // would otherwise clear this never fires, so reset it explicitly.
    this.#isAnimating = false;
    this.#revealObserver?.disconnect();
    this.#revealObserver = null;
    this.removeEventListener('pointerdown', this.#handlePointerDown);
    this.removeEventListener('pointermove', this.#handlePointerMove);
    this.removeEventListener('pointerup', this.#handlePointerUp);
    this.removeEventListener('pointercancel', this.#handlePointerCancel);
    this.removeEventListener('touchmove', this.#handleTouchMove);
    this.removeEventListener('dragstart', this.#handleDragStart);
    this.removeEventListener('click', this.#handleClick, true);

    for (const card of this.#cards) {
      card.classList.remove('exiting');
      card.style.removeProperty('transform');
      card.style.removeProperty('opacity');
      card.style.removeProperty('z-index');
      card.style.removeProperty('--rest-y');
      card.style.removeProperty('--rest-scale');
      card.style.removeProperty('--rest-opacity');
    }

    this.#cards = [];
  }

  /**
   * `offsetHeight` reads a card's plain layout box, unaffected by any
   * transform already applied to it — reliable as the `cardHeight` input to
   * `restStyleForDepth` regardless of which depth that particular card
   * currently happens to be resting at. Every card shares the same
   * crop/width, so any one of them gives the same answer.
   */
  #getCardHeight() {
    return this.#cards[0]?.offsetHeight ?? 0;
  }

  /** @param {number} active */
  #render(active) {
    const total = this.#cards.length;
    const cardHeight = this.#getCardHeight();
    this.#cards.forEach((card, i) => {
      const depth = depthOf(i, active, total);
      applyRest(card, depth, total, cardHeight);
      syncVideoPlayback(card, depth);
    });
  }

  /**
   * `#render()` stamps inline `transform`/`opacity` on every card — if the
   * wrapper is a still-unrevealed `data-reveal-group` (assets/motion.js,
   * gated behind `section.settings.scroll_animations`), those inline styles
   * would permanently mask motion.css's pre-reveal `opacity: 0`, and the
   * entrance animation would silently never play. assets/motion.js reveals by
   * adding its one-shot `motion-revealed` class to each card individually
   * (not to the wrapper) the moment the group scrolls into view — this waits
   * for that on the first card (every card reveals together, as one group)
   * before rendering, and renders immediately when there's nothing to wait
   * for at all: no reveal group, already revealed (e.g. the section was
   * already in view at load), or reduced motion — assets/motion.js never adds
   * the class in that last case either, so waiting for it would mean never
   * rendering at all.
   * @param {Element} wrapper
   */
  #renderOnceRevealed(wrapper) {
    const firstCard = this.#cards[0];
    const alreadySettled =
      !firstCard ||
      !wrapper.hasAttribute('data-reveal-group') ||
      prefersReducedMotion() ||
      firstCard.classList.contains('motion-revealed');

    if (!firstCard || alreadySettled) {
      this.#render(this.#activeIndex);
      this.#playSwipeHint();
      return;
    }

    this.#revealObserver = new MutationObserver(() => {
      if (!firstCard.classList.contains('motion-revealed')) return;
      this.#revealObserver?.disconnect();
      this.#revealObserver = null;
      this.#render(this.#activeIndex);
      this.#playSwipeHint();
    });
    this.#revealObserver.observe(firstCard, { attributes: true, attributeFilter: ['class'] });
  }

  /**
   * One-shot nudge-left-and-back on the active card, so a visitor who has never
   * dragged the deck can still tell it's swipeable. Scheduled `SWIPE_HINT_DELAY_MS`
   * after the stack's first render rather than immediately, so it never overlaps the
   * entrance fade/rise or this file's own resting `transition` on the same
   * `transform` property. `#generation` is captured up front and rechecked when the
   * timeout fires — a breakpoint flip back to desktop in between must not stamp a
   * class onto a card this instance no longer owns.
   */
  #playSwipeHint() {
    if (prefersReducedMotion()) return;

    const generation = this.#generation;
    setTimeout(() => {
      if (this.#generation !== generation) return;
      const card = this.#cards[this.#activeIndex];
      if (!card) return;
      card.addEventListener('animationend', () => card.classList.remove('swipe-hint'), { once: true });
      card.classList.add('swipe-hint');
    }, SWIPE_HINT_DELAY_MS);
  }

  /**
   * Disables the stack's own arrow buttons (sections/editorial-product-stack.liquid)
   * while a card is mid-flight — mirrors the pointer-gesture guard above
   * (`#ignoringGesture`), so a tap can't queue up mid-animation and fire the instant it
   * ends, letting the user rush through the deck out of step with what's rendered.
   * `refs.previous`/`refs.next` resolve from `slideshow-arrow.liquid`'s own `ref="{{
   * action }}"` attribute — this element is their ancestor `Component` even though the
   * buttons live outside the mobile-only card deck itself (see assets/component.js).
   * @param {boolean} disabled
   */
  #setArrowsDisabled(disabled) {
    const refs = /** @type {any} */ (this.refs);
    refs.previous?.toggleAttribute('disabled', disabled);
    refs.next?.toggleAttribute('disabled', disabled);
  }

  #advance() {
    if (this.#isAnimating || this.#cards.length < 2) return;

    const total = this.#cards.length;
    const leavingIndex = this.#activeIndex;
    const nextActive = (this.#activeIndex + 1) % total;
    const leavingCard = /** @type {HTMLElement} */ (this.#cards[leavingIndex]);

    if (prefersReducedMotion()) {
      this.#activeIndex = nextActive;
      this.#render(nextActive);
      return;
    }

    this.#isAnimating = true;
    this.#setArrowsDisabled(true);
    const generation = this.#generation;
    const cardHeight = this.#getCardHeight();

    // Other cards settle via their own CSS `transition`, a separate timeline from the
    // flicking card's `animation` — #isAnimating must wait for both to finish.
    let flickDone = false;
    let settled = false;
    const otherCards = this.#cards.filter((_, i) => i !== leavingIndex);
    let pendingSettles = otherCards.length;
    const trySettle = () => {
      if (settled || !flickDone || pendingSettles > 0) return;
      settled = true;
      this.#isAnimating = false;
      this.#setArrowsDisabled(false);
    };

    // Every other card just gets its new resting depth — the section's own
    // CSS `transition` on transform/opacity smoothly animates that.
    otherCards.forEach((card) => {
      const depth = depthOf(this.#cards.indexOf(card), nextActive, total);
      applyRest(card, depth, total, cardHeight);
      syncVideoPlayback(card, depth);
      card.addEventListener(
        'transitionend',
        (event) => {
          if (event.target !== card || event.propertyName !== 'transform') return;
          pendingSettles--;
          trySettle();
        },
        { once: true },
      );
    });
    // Safety net in case a transitionend is ever missed (backgrounded tab, browser quirk).
    setTimeout(() => {
      if (this.#generation !== generation) return;
      pendingSettles = 0;
      trySettle();
    }, TRANSITION_MS + 100);

    // The leaving card is going from active (depth 0, playing) to some
    // non-zero depth — pause its video immediately rather than waiting for
    // the exit animation to finish, matching every other card's own depth
    // change above.
    syncVideoPlayback(leavingCard, depthOf(leavingIndex, nextActive, total));

    // The leaving card gets the special keyframe flick-and-recycle animation
    // instead, ending exactly at its new (back-of-stack) resting position.
    const restAtBack = restStyleForDepth(depthOf(leavingIndex, nextActive, total), total, cardHeight);
    leavingCard.style.setProperty('--rest-y', `${restAtBack.y}px`);
    leavingCard.style.setProperty('--rest-scale', String(restAtBack.scale));
    leavingCard.style.setProperty('--rest-opacity', String(restAtBack.opacity));

    // Two-phase z-index, timed to match the keyframe animation:
    //  - phase 1 (0% -> EXIT_REVERSAL_FRACTION): the card is sliding out and
    //    rotating — stays in FRONT of everything, one above the highest
    //    resting z-index (`total`, the active card's own), regardless of how
    //    many cards are in the deck.
    //  - phase 2 (EXIT_REVERSAL_FRACTION -> 100%): direction reverses, heading
    //    back to the resting stack — from here on it drops BEHIND every other
    //    card, so it can never visibly pass back over the new active card.
    leavingCard.style.zIndex = String(total + 1);
    leavingCard.classList.add('exiting');

    const reversalTimeout = setTimeout(() => {
      if (this.#generation !== generation) return;
      leavingCard.style.zIndex = String(restAtBack.zIndex);
    }, TRANSITION_MS * EXIT_REVERSAL_FRACTION);

    leavingCard.addEventListener(
      'animationend',
      () => {
        clearTimeout(reversalTimeout);
        if (this.#generation !== generation) return;

        leavingCard.classList.remove('exiting');
        leavingCard.style.removeProperty('--rest-y');
        leavingCard.style.removeProperty('--rest-scale');
        leavingCard.style.removeProperty('--rest-opacity');
        // Lock in the plain resting style so future transitions (driven by
        // the normal `transition` property) start from a clean value.
        applyRest(leavingCard, depthOf(leavingIndex, nextActive, total), total, cardHeight);
        flickDone = true;
        trySettle();
      },
      { once: true },
    );

    this.#activeIndex = nextActive;
  }

  /**
   * The literal time-reversal of `#advance()`: the card already resting at the back of
   * the deck (depth total-1 — exactly where a previous forward step would have sent it)
   * flies back to the front. Plays the same `editorial-product-stack-card-exit`
   * keyframes as the forward flick, just in reverse (`.entering`, section stylesheet) —
   * at reverse-time 0 it's already sitting at its current back pose (the vars below pin
   * that down), so there's no jump, and at reverse-time 100% it lands on the front's
   * identity transform, exactly depth 0. Every other card just gets its new (one deeper)
   * resting depth directly, same as `#advance()`. Unlike that method's leaving card, the
   * entering card here never needs a mid-flight z-index reversal — it's arriving at the
   * front, not heading back into the stack, so it stays on top for the whole flight.
   */
  #retreat() {
    if (this.#isAnimating || this.#cards.length < 2) return;

    const total = this.#cards.length;
    const previousActive = this.#activeIndex;
    const nextActive = (this.#activeIndex - 1 + total) % total;
    const enteringCard = /** @type {HTMLElement} */ (this.#cards[nextActive]);

    if (prefersReducedMotion()) {
      this.#activeIndex = nextActive;
      this.#render(nextActive);
      return;
    }

    this.#isAnimating = true;
    this.#setArrowsDisabled(true);
    const generation = this.#generation;
    const cardHeight = this.#getCardHeight();

    let flightDone = false;
    let settled = false;
    const otherCards = this.#cards.filter((_, i) => i !== nextActive);
    let pendingSettles = otherCards.length;
    const trySettle = () => {
      if (settled || !flightDone || pendingSettles > 0) return;
      settled = true;
      this.#isAnimating = false;
      this.#setArrowsDisabled(false);
    };

    otherCards.forEach((card) => {
      const depth = depthOf(this.#cards.indexOf(card), nextActive, total);
      applyRest(card, depth, total, cardHeight);
      syncVideoPlayback(card, depth);
      card.addEventListener(
        'transitionend',
        (event) => {
          if (event.target !== card || event.propertyName !== 'transform') return;
          pendingSettles--;
          trySettle();
        },
        { once: true },
      );
    });
    setTimeout(() => {
      if (this.#generation !== generation) return;
      pendingSettles = 0;
      trySettle();
    }, TRANSITION_MS + 100);

    syncVideoPlayback(enteringCard, 0);

    // The entering card's current (pre-old-active) resting pose is the flight's start
    // point — the reverse keyframes read it back out of these same custom properties.
    const startDepth = depthOf(nextActive, previousActive, total);
    const startRest = restStyleForDepth(startDepth, total, cardHeight);
    enteringCard.style.setProperty('--rest-y', `${startRest.y}px`);
    enteringCard.style.setProperty('--rest-scale', String(startRest.scale));
    enteringCard.style.setProperty('--rest-opacity', String(startRest.opacity));

    enteringCard.style.zIndex = String(total + 1);
    enteringCard.classList.add('entering');

    enteringCard.addEventListener(
      'animationend',
      () => {
        if (this.#generation !== generation) return;

        enteringCard.classList.remove('entering');
        enteringCard.style.removeProperty('--rest-y');
        enteringCard.style.removeProperty('--rest-scale');
        enteringCard.style.removeProperty('--rest-opacity');
        applyRest(enteringCard, 0, total, cardHeight);
        flightDone = true;
        trySettle();
      },
      { once: true },
    );

    this.#activeIndex = nextActive;
  }

  /**
   * Public entry points for the stack's arrow controls
   * (sections/editorial-product-stack.liquid, wired via the standard `on:click="/{{
   * action }}"` declarative binding — see assets/component.js) — named to match
   * `slideshow-arrow.liquid`'s own action names ('previous'/'next') rather than this
   * file's internal `#advance`/`#retreat` terminology, so the shared snippet can be
   * reused unmodified. Mapped by the *flourish* each arrow plays, not by its name:
   * `slideshow-arrow.liquid` flips 'previous' to point left and leaves 'next' pointing
   * right, so the left-pointing arrow plays the same flourish as a leftward swipe
   * (`#advance`) and the right-pointing arrow plays the same flourish as a rightward
   * swipe (`#retreat`) — each arrow's pointing direction always matches the drag that
   * produces the same animation.
   */
  next() {
    this.#retreat();
  }

  previous() {
    this.#advance();
  }

  /** @param {PointerEvent} event */
  #handlePointerDown = (event) => {
    // A second touch (e.g. a pinch) must never overwrite the tracked start
    // point of the gesture already in progress — `touch-action: pan-y`
    // excludes pinch-zoom, so the browser won't intercept it as its own
    // gesture and rescue us with a `pointercancel`.
    if (!event.isPrimary) return;
    // A real gesture starting supersedes the hint outright, whether or not it's
    // still mid-animation — avoids the class fighting `.exiting`'s own `animation`
    // if `#advance()` runs on this same (active) card before the hint finishes.
    this.#cards[this.#activeIndex]?.classList.remove('swipe-hint');
    // A card is still mid-flight from the last swipe (or an arrow click) — ignore this
    // entire gesture rather than tracking it, so it can't be judged a swipe once the
    // in-flight animation finishes (`#advance`/`#retreat` already no-op while animating,
    // but leaving the gesture tracked regardless can still misfire `#handleTouchMove`'s
    // scroll suppression or `#handleClick`'s tap/drag judgement — see `#ignoringGesture`).
    this.#ignoringGesture = this.#isAnimating;
    if (this.#ignoringGesture) return;
    this.#pointerDownX = event.clientX;
    this.#pointerDownY = event.clientY;
    // A fresh gesture always starts a clean slate — without this, a click
    // that follows a swipe with no new pointer gesture in between (keyboard
    // Enter/Space, assistive-tech activation) would still be judged by the
    // *previous* swipe's value and get killed by `#handleClick` below.
    this.#wasDrag = false;
    this.#dragDirection = null;
  };

  /** @param {PointerEvent} event */
  #handlePointerMove = (event) => {
    if (!event.isPrimary || this.#ignoringGesture) return;
    if (this.#dragDirection !== null) return; // already locked for this gesture

    const dx = event.clientX - this.#pointerDownX;
    const dy = event.clientY - this.#pointerDownY;
    if (Math.max(Math.abs(dx), Math.abs(dy)) < DIRECTION_LOCK_MOVE_PX) return;
    this.#dragDirection = Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical';
  };

  /**
   * Cancels the section's native `touch-action: pan-y` scroll for gestures
   * `#handlePointerMove` locked "horizontal" — a real `TouchEvent`, not the
   * `PointerEvent` driving the rest of this component's gesture tracking,
   * because iOS Safari doesn't reliably honor `preventDefault()` called from
   * `pointermove` to cancel an in-progress (or about-to-start) native pan; it
   * does honor it here. Runs after `#handlePointerMove` for the same physical
   * move (browsers dispatch the pointer event first), so `#dragDirection`
   * already reflects this event's own movement.
   * @param {TouchEvent} event
   */
  #handleTouchMove = (event) => {
    if (this.#dragDirection === 'horizontal') event.preventDefault();
  };

  /** @param {PointerEvent} event */
  #handlePointerUp = (event) => {
    if (!event.isPrimary) return;
    if (this.#ignoringGesture) {
      this.#ignoringGesture = false;
      this.#dragDirection = null;
      return;
    }

    const dx = event.clientX - this.#pointerDownX;
    const dy = event.clientY - this.#pointerDownY;

    this.#wasDrag = Math.abs(dx) > TAP_MOVE_TOLERANCE_PX || Math.abs(dy) > TAP_MOVE_TOLERANCE_PX;

    // A drag that's more vertical than horizontal is left alone so the page can
    // scroll; otherwise a clear leftward drag advances and a clear rightward one
    // retreats, each moving exactly one card — same convention as
    // assets/collection-reel.js's own bidirectional swipe.
    if (Math.abs(dx) > Math.abs(dy)) {
      if (dx < -SWIPE_THRESHOLD_PX) this.#advance();
      else if (dx > SWIPE_THRESHOLD_PX) this.#retreat();
    }
    this.#dragDirection = null;
  };

  /** @param {PointerEvent} event */
  #handlePointerCancel = (event) => {
    if (!event.isPrimary) return;
    this.#ignoringGesture = false;
    this.#dragDirection = null;
  };

  /** @param {DragEvent} event */
  #handleDragStart = (event) => {
    event.preventDefault();
  };

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    if (!this.#wasDrag) return;
    // A swipe that ends over the card's image link would otherwise still
    // navigate to the product page once native drag is suppressed above.
    event.preventDefault();
    event.stopPropagation();
  };
}

if (!customElements.get('editorial-product-stack-cards')) {
  customElements.define('editorial-product-stack-cards', EditorialProductStackCards);
}
